import { AnyAction, createSelector, ThunkDispatch } from "@reduxjs/toolkit";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormFeedback,
  Spinner,
} from "reactstrap";
// import { resendOtp, verifyOtp } from "slices/thunks";
import { resendOtp,verifyOtp } from "slices/form/thunk";
import { RootState } from "store";

export interface OtpModalProps {
  isOpen: boolean;
  isverified:boolean;
  toggle: () => void;
  onVerify: (code: string) => Promise<void> | void;
  isVerifying?: boolean;
  error?: string;
  phoneMasked?: string;
  showmodal:any;
  setshowmodal:any;
  formik:any;
}

const OtpModal: React.FC<OtpModalProps> = ({
 showmodal,
 setshowmodal,
  isOpen,
  isverified,
  toggle,
  onVerify,
  formik,
  // isVerifying = false,
  // error1 = "",
  phoneMasked = "",
}) => {
  const LENGTH = 5; // changed to 5-digit OTP
  const [values, setValues] = useState<string[]>(Array(LENGTH).fill(""));
  const [touched, setTouched] = useState(false);
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState<string>("");
  const [isVerifying, setIsVerifying] = useState(false);
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();
  useEffect(() => {
    if (isOpen) {
      setValues(Array(LENGTH).fill(""));
      setTouched(false);
      setIsVerified(false);
      setError("");
      setTimeout(() => {
        inputsRef.current?.[0]?.focus();
      }, 50);
    }
  }, [isOpen]);

  const code = useMemo(() => values.join(""), [values]);
  const isComplete = code.length === LENGTH && values.every((v) => v !== "");

  const setValueAt = (idx: number, val: string) => {
    setValues((prev) => {
      const next = [...prev];
      next[idx] = val;
      return next;
    });
  };

  const selectLeadState = (state: RootState) => state.Lead;

  const selectUserData = createSelector(selectLeadState, (lead) => ({
    publicGuid: lead?.publicGuid,
  }));
  
  const { publicGuid } = useSelector(selectUserData);

  const handleChange = (idx: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const digit = raw.replace(/\D/g, "").slice(-1) || "";
    setValueAt(idx, digit);

    if (digit && idx < LENGTH - 1) {
      inputsRef.current?.[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    const key = e.key;

    if (key === "Backspace") {
      if (values[idx]) {
        setValueAt(idx, "");
      } else if (idx > 0) {
        inputsRef.current?.[idx - 1]?.focus();
        setValueAt(idx - 1, "");
      }
    }

    if (key === "ArrowLeft" && idx > 0) {
      inputsRef.current?.[idx - 1]?.focus();
    }
    if (key === "ArrowRight" && idx < LENGTH - 1) {
      inputsRef.current?.[idx + 1]?.focus();
    }
  };

  const handlePaste = (idx: number, e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = (e.clipboardData.getData("text") || "").replace(/\D/g, "");
    if (!pasted) return;

    const chars = pasted.slice(0, LENGTH).split("");
    setValues((prev) => {
      const next = [...prev];
      let j = idx;
      for (let i = 0; i < chars.length && j < LENGTH; i++, j++) {
        next[j] = chars[i];
      }
      return next;
    });

    const lastIndex = Math.min(idx + chars.length - 1, LENGTH - 1);
    inputsRef.current?.[lastIndex]?.focus();
  };

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault?.();
    setTouched(true);
    if (!isComplete) return;
    setIsVerifying(true);
    setIsVerified(false);
    setError("");

    try {
      const otp = {
        guid: publicGuid,
        otp:code
      }
      console.log("otp",otp)
      const result: any = await dispatch(verifyOtp(otp));
      if (result?.payload?.data?.success) {
        setIsVerified(true);
        setError("");
        setshowmodal(true);
        formik.resetForm();
      } else {
        toast.error(result.error.message)
        // setError(result.error.message);
      }
    } catch (err) {
      // setError("Something went wrong, try again");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async (e: React.MouseEvent) => {
  e.preventDefault();
  setError("");
  setIsVerifying(true);

  try {
    const resendPayload = {
      guid:publicGuid
    }
    const result: any = await dispatch(resendOtp(resendPayload));
    if (result?.payload?.success) {
      setError(""); 
    } else {
      toast.error(result.payload.data.message)
      // setError(result.payload.data.message);
    }
  } catch (err) {
    setError("Something went wrong, try again");
  } finally {
    setIsVerifying(false);
  }
};

  const showInvalid = touched && !isComplete;
 

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>Verify OTP</ModalHeader>
      <Form onSubmit={submit} noValidate>
        <ModalBody>
          {phoneMasked ? (
            <p className="text-muted mb-2">
              We sent a 5-digit code to <strong>{phoneMasked}</strong>.
            </p>
          ) : (
            <p className="text-muted mb-2">
              Enter the 5-digit code we sent to your phone/email.
            </p>
          )}

          <div className="d-flex gap-2 justify-content-center my-2">
            {Array.from({ length: LENGTH }).map((_, i) => (
              <input
                key={i}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                className="form-control text-center"
                style={{
                  width: 56,
                  height: 56,
                  fontSize: 22,
                  fontWeight: 600,
                  letterSpacing: 2,
                }}
                maxLength={1}
                value={values[i]}
                onChange={(e) => handleChange(i, e)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                onPaste={(e) => handlePaste(i, e)}
                ref={(el) => (inputsRef.current[i] = el)}
                aria-label={`OTP digit ${i + 1}`}
              />
            ))}
          </div>

          {showInvalid && (
            <FormFeedback style={{ display: "block" }}>
              Please enter all 5 digits.
            </FormFeedback>
          )}
          {error ? (
            <FormFeedback style={{ display: "block" }} className="mt-2">
              {error}
            </FormFeedback>
          ) : null}

          <div className="d-flex justify-content-between align-items-center mt-3">
            <small className="text-muted">
              Didn’t get a code?{" "}
              {/* <a href="#" onClick={(e) => e.preventDefault()}> */}
              <a onClick={handleResend}>
                Resend
              </a>
            </small>
            {/* <small className="text-muted">
              Wrong number?{" "}
              <a href="#" onClick={(e) => e.preventDefault()}>
                Change
              </a>
            </small> */}
          </div>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-between">
          <Button type="button" color="secondary" onClick={toggle} disabled={isVerifying}>
            Cancel
          </Button>
          <Button type="submit" color="primary" disabled={isVerifying || !isComplete} >
            {isVerifying ? (
              <>
                <Spinner size="sm" className="me-2" /> Verifying...
              </>
            ) : (
              "Verify"
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default OtpModal;
